import type { UI } from '@/v2/types';
import React from 'react';
import { Box, Flex, Heading, Text } from '@qga/roo-ui/components';

import { BEST_RATE_TEXT } from '@shared/v2/constants/packages';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import PackageHeroSummary from '@/components/PackageHeroSummary';
import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
import usePropertyContent from '@/components/PackagePage/hooks/usePropertyContent';
import withProps from '@/shared/components/withProps';
import { PackagePagePriceWithPointsPlusPay } from '@/v2/components/pages/PackageView/components/PackagePagePrice';
import PriceContainer from '@/v2/components/pages/PackageView/components/RoomOptions/components/RoomOption/components/CTA/components/PriceContainer';
import Inclusion from '@/v2/components/ui/Inclusion';
import PackageDetails from '@/v2/components/ui/PackageDetails';
import Inclusions from './components/Inclusions';

export type RoomOptionDetailsProps = {
  packageName?: string;
  offer: UI.RoomOptionOffer;
  roomName: string;
  title?: string;
  limitInclusions?: boolean;
};

// @ts-expect-error withProps doesn't have correct types atm
const SectionTitle: typeof Text = withProps({
  fontSize: 'sm',
  as: 'div',
})(Text);

const RoomOptionDetails = ({
  packageName = BEST_RATE_TEXT,
  offer,
  title = 'Your package option:',
  roomName,
}: RoomOptionDetailsProps) => {
  const propertyContent = usePropertyContent();
  const procuredOffer = useProcuredOffer();
  const { searchQuery } = useSearchQuery();
  const { adults, children, infants } = searchQuery || {};

  return (
    <Flex
      gap="4"
      flexDirection="column"
      data-testid="PACKAGE_OPTION_ROOM_OPTION_DETAILS"
    >
      <Box>
        <SectionTitle mb="1">{title}</SectionTitle>
        <Heading.h4 fontSize={['md', null, 'lg']} mb="0">
          {packageName}
        </Heading.h4>
      </Box>
      <Box>
        <SectionTitle mb="1">Room type:</SectionTitle>
        <Inclusion inclusion={{ name: roomName, code: 'hotel' }} />
      </Box>
      <Flex flexDirection="column" alignItems="flex-start" gap="3">
        <SectionTitle>Package inclusions:</SectionTitle>
        <Inclusions offer={offer} />
        <PackageDetails
          propertyContent={propertyContent}
          procuredOffer={procuredOffer}
          inclusions={offer?.leadInOffer?.inclusions}
        />
      </Flex>
      <Box mt="2">
        <PriceContainer includeDivider={false}>
          <PackageHeroSummary searchQuery={searchQuery} showNights exact />
          <PackagePagePriceWithPointsPlusPay
            charges={offer.charges}
            travellers={{ adults, children, infants }}
            offerId={offer.hotelOffer.id}
          />
        </PriceContainer>
      </Box>
    </Flex>
  );
};

export default RoomOptionDetails;
